/**
 * A thin same-origin fetch for the first-party `/auth/*` routes (controlplane-auth task 12.1).
 *
 * The auth endpoints (`/auth/login`, `/auth/start`, `/auth/verify`, `/auth/forgot`, `/auth/reset`,
 * `/auth/invite/redeem`, `/auth/logout`, `/auth/switch-team`, `/auth/session`) are TOP-LEVEL on the
 * BFF origin — NOT under `/api/v1` — so this wrapper is separate from `apiFetch` (which owns the
 * `/api/v1` surface, `lib/api.ts`). It mirrors `apiFetch`'s credential/JSON/error handling exactly:
 *   - same-origin credentials so the `oa_session` (and the OIDC flow) cookies ride the request,
 *   - a JSON `Accept`, JSON body only when provided,
 *   - the same {@link ApiError} classes keyed off the HTTP status, carrying the server `error.code`
 *     when the body is the BFF/management `{ error: { code, message, request_id } }` envelope.
 *
 * The login screen (task 12) reads `error.code` / `error.kind` from the thrown {@link ApiError} and
 * runs it through `screens/auth/authErrors.ts` to show a friendly, non-revealing inline message. The
 * SERVER-SIDE SESSION model is preserved: the browser never holds a token; a successful POST sets
 * the `oa_session` cookie server-side and this wrapper just resolves the JSON body.
 *
 * NOTE (backend wiring gap, Phase 7 / task 15): the Phase-2 `/auth/*` handlers exist in the worker
 * (`login.ts`, `email-otp.ts`, `oidc.ts`) but only `/auth/switch-team` is routed in `index.ts`
 * today. The login screen posts to these paths; the routing is wired at the Phase 7 cutover. Until
 * then an unrouted path falls to the asset router and resolves to the SPA shell HTML, which this
 * wrapper surfaces as a `malformed` error (not valid JSON) — a safe, friendly "try again".
 */

import { ApiError, type ApiErrorBody } from "./api.ts";

/** Options for a single `/auth/*` request. */
export interface AuthRequestInit {
  readonly method?: string;
  /** A JSON body; serialised and sent with `content-type: application/json`. */
  readonly body?: unknown;
  /** Query parameters appended to the path (undefined values omitted). */
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
}

const REQUEST_ID_HEADER = "x-request-id";

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  const e = error as Record<string, unknown>;
  return typeof e["code"] === "string" && typeof e["message"] === "string";
}

function parseRetryAfter(headerValue: string | null): number | null {
  if (headerValue === null) return null;
  const seconds = Number(headerValue);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function buildPath(path: string, query: AuthRequestInit["query"]): string {
  if (query === undefined) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${path}?${qs}` : path;
}

/**
 * Perform an `/auth/*` call and return the parsed JSON body typed as `T`, or throw an
 * {@link ApiError}. Unlike {@link apiFetch}, a 401 here is thrown (as `unauthorized`) rather than
 * being treated specially — the login screen maps it to a friendly credential message. The one
 * consumer that treats 401 as "no session" (the session read) handles that itself.
 */
export async function authFetch<T>(path: string, init: AuthRequestInit = {}): Promise<T> {
  const url = buildPath(path, init.query);
  const headers = new Headers({ accept: "application/json" });
  let requestBody: string | undefined;
  if (init.body !== undefined) {
    headers.set("content-type", "application/json");
    requestBody = JSON.stringify(init.body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: init.method ?? "GET",
      headers,
      credentials: "same-origin",
      ...(requestBody === undefined ? {} : { body: requestBody }),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    });
  } catch (cause) {
    throw new ApiError("network", "The request could not be sent.", { cause });
  }

  const requestId = response.headers.get(REQUEST_ID_HEADER);

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (text.length === 0) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch (cause) {
      throw new ApiError("malformed", "The server returned a response that was not valid JSON.", {
        status: response.status,
        requestId,
        cause,
      });
    }
  }

  // Error path: read the envelope if present so we can carry the opaque code + request id.
  let code: string | null = null;
  let message = `Request failed with status ${response.status}.`;
  try {
    const parsed: unknown = await response.json();
    if (isApiErrorBody(parsed)) {
      code = parsed.error.code;
      message = parsed.error.message;
    }
  } catch {
    // A non-JSON error body is fine; the status classifies the failure below.
  }

  const retryAfterSeconds = parseRetryAfter(response.headers.get("retry-after"));
  const base = { status: response.status, code, requestId, retryAfterSeconds } as const;

  if (response.status === 503) throw new ApiError("waking", message, base);
  if (response.status === 429) throw new ApiError("request", message, { ...base, code: code ?? "locked" });
  if (response.status === 401) throw new ApiError("unauthorized", message, base);
  if (response.status === 403) throw new ApiError("forbidden", message, base);
  if (response.status === 404) throw new ApiError("not_found", message, base);
  if (response.status >= 500) throw new ApiError("server", message, base);
  throw new ApiError("request", message, base);
}
